// src/features/players/components/PlayersPage.tsx

import { useCallback, useMemo, useState } from 'react';
import {
  Stack, Title, Text, Group, Button, Loader, Alert, TextInput,
} from '@mantine/core';
import { useDebouncedValue, useHotkeys, useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useNavigate } from '@tanstack/react-router';
import { IconAlertCircle, IconPlus, IconSearch, IconX } from '@tabler/icons-react';
import fuzzysort from 'fuzzysort';
import { bcLong } from '@lib2/model/util/string/BaseConverter';
import { usePlayers } from '../usePlayers';
import { PlayerUtils } from '../PlayerUtils';
import {
  useActivatePlayers,
  useDeactivatePlayers,
  useDeletePlayers,
} from '../usePlayerMutations';
import { SelectionProvider, useSelection } from '@/shared/contexts/SelectionContext';
import { PlayerTable } from './PlayerTable';
import { CreatePlayerModal } from './CreatePlayerModal';
import { PLAYER_GRID } from '@/lib/constants/player';
import type { PlayerGridRow } from '@/lib/types/Player';
import type { PlayerSortKey } from '@/lib/constants/player';

type SortDirection = 'asc' | 'desc';

function PlayersPageContent() {
  const navigate = useNavigate();
  const { players, isLoading, isError, error } = usePlayers();
  const { selectedIds, selectedCount, clearSelection } = useSelection();
  const [createModalOpened, { open: openCreateModal, close: closeCreateModal }] = useDisclosure(false);

  const [searchText, setSearchText] = useState('');
  const [sortKey, setSortKey] = useState<PlayerSortKey>('label');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const activateMutation = useActivatePlayers();
  const deactivateMutation = useDeactivatePlayers();
  const deleteMutation = useDeletePlayers();

  useHotkeys([['Escape', () => clearSelection()]]);

  const [debouncedSearch] = useDebouncedValue(searchText, PLAYER_GRID.SEARCH_DEBOUNCE_MS);

  // Build grid rows from player data
  const gridRows = useMemo(() => PlayerUtils.toGridRows(players), [players]);

  // Filter by search
  const filteredRows = useMemo(() => {
    if (!debouncedSearch.trim()) return gridRows;

    const results = fuzzysort.go(debouncedSearch, gridRows, {
      keys: ['label', 'descShort'],
      threshold: -10000,
    });
    return results.map(r => r.obj);
  }, [gridRows, debouncedSearch]);

  // Sort
  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'label':
          cmp = a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
          break;
        case 'enabled':
          cmp = (a.enabled === b.enabled) ? 0 : a.enabled ? -1 : 1;
          break;
        case 'created':
          cmp = a.createdFormatted.localeCompare(b.createdFormatted);
          // Use tooltip (full timestamp) for accurate sort
          cmp = a.createdTooltip.localeCompare(b.createdTooltip);
          break;
        case 'updated':
          cmp = a.updatedTooltip.localeCompare(b.updatedTooltip);
          break;
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [filteredRows, sortKey, sortDirection]);

  const handleSort = useCallback((key: PlayerSortKey) => {
    if (key === sortKey) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  }, [sortKey]);

  // --- Bulk action handlers ---

  const handleBulkActivate = useCallback(() => {
    const ids = Array.from(selectedIds);
    activateMutation.mutate(ids, {
      onSuccess: () => {
        notifications.show({ title: 'Activated', message: `${ids.length} player(s) activated.`, color: 'green' });
        clearSelection();
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  }, [selectedIds, activateMutation, clearSelection]);

  const handleBulkDeactivate = useCallback(() => {
    const ids = Array.from(selectedIds);
    deactivateMutation.mutate(ids, {
      onSuccess: () => {
        notifications.show({ title: 'Deactivated', message: `${ids.length} player(s) deactivated.`, color: 'green' });
        clearSelection();
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  }, [selectedIds, deactivateMutation, clearSelection]);

  const handleBulkDelete = useCallback(() => {
    const ids = Array.from(selectedIds);
    modals.openConfirmModal({
      title: 'Delete Players',
      children: (
        <Text size="sm">
          Are you sure you want to delete {ids.length} player(s)? This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteMutation.mutate(ids, {
          onSuccess: () => {
            notifications.show({ title: 'Deleted', message: `${ids.length} player(s) deleted.`, color: 'green' });
            clearSelection();
          },
          onError: (err) => {
            notifications.show({ title: 'Error', message: err.message, color: 'red' });
          },
        });
      },
    });
  }, [selectedIds, deleteMutation, clearSelection]);

  // --- Row action handlers ---

  const handleCopy = useCallback((row: PlayerGridRow) => {
    // Open create modal pre-populated with the source player for copy
    openCreateModal();
  }, [openCreateModal]);

  const handleActivateToggle = useCallback((row: PlayerGridRow) => {
    const mutation = row.enabled ? deactivateMutation : activateMutation;
    const action = row.enabled ? 'Deactivated' : 'Activated';
    mutation.mutate([row.playerId], {
      onSuccess: () => {
        notifications.show({ title: action, message: `"${row.label}" ${action.toLowerCase()}.`, color: 'green' });
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  }, [activateMutation, deactivateMutation]);

  const handleRowDelete = useCallback((row: PlayerGridRow) => {
    modals.openConfirmModal({
      title: `Delete "${row.label}"?`,
      children: (
        <Text size="sm">
          This action cannot be undone. Are you sure you want to delete this player?
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteMutation.mutate([row.playerId], {
          onSuccess: () => {
            notifications.show({ title: 'Deleted', message: `"${row.label}" deleted.`, color: 'green' });
          },
          onError: (err) => {
            notifications.show({ title: 'Error', message: err.message, color: 'red' });
          },
        });
      },
    });
  }, [deleteMutation]);

  const handleCreateSuccess = useCallback((player: { core: { playerId: number } }) => {
    const displayId = bcLong.getStr(player.core.playerId);
    navigate({ to: '/players/$id', params: { id: displayId } });
  }, [navigate]);

  if (isLoading) {
    return (
      <Stack align="center" justify="center" h={400}>
        <Loader size="lg" />
        <Text c="dimmed">Loading players...</Text>
      </Stack>
    );
  }

  if (isError) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
        {error?.message ?? 'Failed to load players'}
      </Alert>
    );
  }

  return (
    <Stack gap="md" p="md" h="100%">
      <Group justify="space-between">
        <Title order={2}>Players</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreateModal}>
          Create Player
        </Button>
      </Group>

      {/* Search + Bulk Actions toolbar */}
      <Group gap="sm">
        <TextInput
          placeholder="Search players..."
          leftSection={<IconSearch size={16} />}
          rightSection={searchText ? (
            <IconX
              size={14}
              style={{ cursor: 'pointer' }}
              onClick={() => setSearchText('')}
            />
          ) : undefined}
          value={searchText}
          onChange={(e) => setSearchText(e.currentTarget.value)}
          style={{ flex: 1, maxWidth: 360 }}
        />

        {players.length > 0 && (
          <Text size="sm" c="dimmed">
            {filteredRows.length === players.length
              ? `${players.length} player${players.length === 1 ? '' : 's'}`
              : `${filteredRows.length} of ${players.length}`
            }
          </Text>
        )}

        {selectedCount > 0 && (
          <Group gap="xs" ml="auto">
            <Text size="sm" fw={500}>{selectedCount} selected</Text>
            <Button
              size="xs"
              variant="light"
              onClick={handleBulkActivate}
              loading={activateMutation.isPending}
            >
              Activate
            </Button>
            <Button
              size="xs"
              variant="light"
              onClick={handleBulkDeactivate}
              loading={deactivateMutation.isPending}
            >
              Deactivate
            </Button>
            <Button
              size="xs"
              variant="light"
              color="red"
              onClick={handleBulkDelete}
              loading={deleteMutation.isPending}
            >
              Delete
            </Button>
            <IconX
              size={16}
              style={{ cursor: 'pointer' }}
              onClick={clearSelection}
            />
          </Group>
        )}
      </Group>

      <PlayerTable
        rows={sortedRows}
        sortKey={sortKey}
        sortDirection={sortDirection}
        onSort={handleSort}
        onCopy={handleCopy}
        onActivateToggle={handleActivateToggle}
        onDelete={handleRowDelete}
      />

      <CreatePlayerModal
        opened={createModalOpened}
        onClose={closeCreateModal}
        players={players}
        onSuccess={handleCreateSuccess}
      />
    </Stack>
  );
}

export function PlayersPage() {
  return (
    <SelectionProvider>
      <PlayersPageContent />
    </SelectionProvider>
  );
}
